import React, { useState, useContext, useRef } from "react";
import { View, StyleSheet } from "react-native";
import colors from "../../config/colors";
import NtActivityIndicator from "../../components/NtActivityIndicator";
import UserContext from "../../context/UserContext";
import { useTheme } from "../../context/ThemeContext";
import NtModalUnactiveButton from "../../components/NtModalUnactiveButton";
import NtModalActiveButton from "../../components/NtModalActiveButton";
import NtModalInputField from "../../components/NtModalInputField";
import NtModalTitle from "../../components/NtModalTitle";
import NtModalSubtitle from "../../components/NtModalSubtitle";
import NtModalError from "../../components/NtModalError";
import NtModal from "../../components/NtModal";
import NtModalConfirmAlert from "../../components/NtModalConfirmAlert";
import NtDialplanXmlEditor from "./NtDialplanXmlEditor";
import Logger from "../../common/utils/Logger";
import { parseError } from "@ntconnect/framework/pbx";
import {
  createDialplan,
  updateDialplan,
  deleteDialplan,
} from "../../api/dialplan";

function NtDialplanForm({ item, onCancel, onSave, onDelete }) {
  const context = useContext(UserContext);
  const { theme } = useTheme();
  const isEdit = !!item;
  const confirmRef = useRef();
  const xmlModalRef = useRef();

  const [name, setName] = useState(item?.dialplan_name || "");
  const [number, setNumber] = useState(item?.dialplan_number || "");
  const [contextField, setContextField] = useState(item?.dialplan_context || "");
  const [description, setDescription] = useState(item?.dialplan_description || "");
  const [order, setOrder] = useState(
    item?.dialplan_order != null ? String(item.dialplan_order) : "",
  );

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const id = item?.dialplan_uuid;
  const domain = context.user?.domain;

  const isRequiredFields = () => !!number;

  const handleSave = async () => {
    setLoading(true);
    setError(null);

    const params = {
      dialplan_name: name || undefined,
      dialplan_number: number,
      dialplan_context: contextField || undefined,
      dialplan_description: description || undefined,
      dialplan_order: order ? parseInt(order, 10) : undefined,
    };

    const response = isEdit
      ? await updateDialplan(domain, id, params)
      : await createDialplan(domain, params);

    Logger("Dialplan Save", response);

    if (!response.ok) {
      setError(parseError(response));
      setLoading(false);
      return;
    }

    setLoading(false);
    onSave?.(response.data);
  };

  const handleDelete = async () => {
    setLoading(true);
    setError(null);

    const response = await deleteDialplan(domain, id);
    Logger("Dialplan Delete", response);

    if (response.ok) {
      onDelete?.();
    } else {
      setError(parseError(response));
    }

    setLoading(false);
  };

  return (
    <View style={styles.container}>
      <NtModalTitle>
        {isEdit ? "Update Dialplan" : "Add new Dialplan"}
      </NtModalTitle>
      <NtModalSubtitle style={{ marginTop: 4 }}>
        {isEdit
          ? "Update or remove this dialplan."
          : "Create a custom dialplan entry."}
      </NtModalSubtitle>

      <NtModalInputField
        containerStyle={{ marginTop: 25 }}
        title={"Name"}
        placeholder={"i.e. Custom IVR Entry"}
        value={name}
        setValue={setName}
      />
      <NtModalInputField
        containerStyle={{ marginTop: 15 }}
        title={"Number *"}
        placeholder={"Pattern or extension"}
        value={number}
        setValue={setNumber}
      />
      <NtModalInputField
        containerStyle={{ marginTop: 15 }}
        title={"Context"}
        placeholder={"Context"}
        value={contextField}
        setValue={setContextField}
      />
      <NtModalInputField
        containerStyle={{ marginTop: 15 }}
        title={"Description"}
        placeholder={"Description"}
        value={description}
        setValue={setDescription}
      />
      <NtModalInputField
        containerStyle={{ marginTop: 15 }}
        title={"Order"}
        placeholder={"0-999"}
        value={order}
        setValue={setOrder}
      />

      <NtModalError error={error} containerStyle={{ marginTop: 6 }} />

      <View style={styles.footer}>
        {isEdit ? (
          <View style={styles.footerLeft}>
            <NtModalActiveButton
              containerStyle={{ backgroundColor: theme.red }}
              title={"Delete"}
              icon={"delete"}
              onPress={() => confirmRef.current?.show()}
              enabled={true}
            />
            <NtModalActiveButton
              containerStyle={{ marginLeft: 10 }}
              title={"Edit XML"}
              icon={"xml"}
              onPress={() => xmlModalRef.current?.show()}
              enabled={true}
            />
          </View>
        ) : (
          <View />
        )}

        <View style={styles.footerRight}>
          {loading && (
            <NtActivityIndicator
              color={colors.blue}
              size="small"
              containerStyle={{ marginRight: 25 }}
            />
          )}
          <NtModalUnactiveButton title={"Cancel"} onPress={onCancel} />
          <NtModalActiveButton
            containerStyle={{ marginLeft: 15 }}
            title={isEdit ? "Update" : "Create"}
            onPress={() => {
              if (isRequiredFields()) handleSave();
            }}
            enabled={isRequiredFields()}
          />
        </View>
      </View>

      <NtModal ref={confirmRef}>
        <NtModalConfirmAlert
          title="Delete"
          message="Are you sure you want to delete this dialplan? This action is permanent and cannot be undone."
          onCancel={() => confirmRef.current?.dismiss()}
          onAccept={() => {
            confirmRef.current?.dismiss();
            handleDelete();
          }}
        />
      </NtModal>

      {isEdit ? (
        <NtModal ref={xmlModalRef}>
          <NtDialplanXmlEditor
            item={item}
            onCancel={() => xmlModalRef.current?.dismiss()}
            onSave={() => {
              xmlModalRef.current?.dismiss();
              onSave?.();
            }}
          />
        </NtModal>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    minWidth: 500,
    maxWidth: 600,
    padding: 15,
  },
  footer: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginTop: 25,
  },
  footerLeft: { flexDirection: "row", alignItems: "center" },
  footerRight: { flexDirection: "row", alignItems: "center" },
});

export default NtDialplanForm;
